import { useContext, useMemo } from 'react';
import Summary from '../../../ui-components/Summary';
import { bookingState } from '../client-state/booking-state-context';
import useLocalize from '../../../Hooks/useLocalize';
import useBookingPrices from '../../../Hooks/use-booking-prices';
import { Heading } from '../../../SubComponents/Text';
import FlexibleRoutingSummary from '../../../ui-components/summary-sections/flexible-routing-summary';
import { formatBookingDate } from '../../../Utils/dates';
import useLocale from '../../../Hooks/use-locale';
import { checkFeatureFlag } from '../../../Utils/utils';
import { FeatureName } from '../../../Utils/graphql-sdk';
import { DateTime } from 'luxon';
import { paymentReducerActionTypes } from '../../../redusers/payment-reducer';
import useBookingMinimalShop from '../server-data/use-booking-minimal-shop';
import useBookingShop from '../server-data/use-booking-shop';
import useBookingDockingLocations from '../server-data/use-booking-docking-locations';
import BoatCountDurationSummary from '../../../ui-components/summary-sections/boat-count-duration-summary';
import PeopleDurationSummary from '../../../ui-components/summary-sections/people-duration-summary';
import { shopIds } from '../../../shop-ids';

interface SummaryWithDataProps {
  closeable: boolean;
  editable: boolean;
}

const SummaryWithData = (props: SummaryWithDataProps) => {
  const { data: minimalShop } = useBookingMinimalShop();
  const { data: shop } = useBookingShop();
  const { data: dockingLocations } = useBookingDockingLocations();
  const { state, dispatch } = useContext(bookingState);
  const localize = useLocalize();
  const { locale, language } = useLocale();
  const { bookingPrices } = useBookingPrices();

  const sailoutTimeFormatted = useMemo(() => {
    if (checkFeatureFlag(shop ?? {}, FeatureName.LocalizedTimeDisplay)) {
      if (state.booking.date) {
        return DateTime.fromISO(state.booking.date)
          .setZone(shop?.timezoneName)
          .setLocale(locale)
          .toLocaleString(DateTime.TIME_SIMPLE);
      }

      return null;
    }

    // Fallback to raw start time
    const fallback: string | null = state.booking.startTime ?? null;

    return fallback;
  }, [locale, shop, state.booking.date, state.booking.startTime]);

  const returnTimeFormatted = useMemo(() => {
    if (checkFeatureFlag(shop ?? {}, FeatureName.LocalizedTimeDisplay)) {
      if (state.booking.date) {
        return DateTime.fromISO(state.booking.date)
          .plus({ hours: state.booking.duration })
          .setZone(shop?.timezoneName)
          .setLocale(locale)
          .toLocaleString(DateTime.TIME_SIMPLE);
      }

      return null;
    }

    // Fallback to raw start time
    const fallback: string | null = state.booking.endTime ?? null;

    return fallback;
  }, [locale, shop, state.booking.date, state.booking.endTime, state.booking.duration]);

  return (
    <Summary
      closable={props.closeable}
      orderEditable={props.editable && state.currentStep >= 3}
      heading={localize('product.yourBooking')}
      shopName={minimalShop?.name || localize('product.noLocationSelected')}
      prices={bookingPrices}
      order={state.order}
      items={shop?.items}
      vouchers={state.vouchers}
      payments={state.payment.payments}
      isWarrantyApplied={state.isWarrantyApplied}
      warrantyEditable={props.editable}
      modifiedDate={state.booking.modifiedDate}
      expiresDate={state.booking.reservedTimeslotExpires}
      showBookingTimer={!state.payment.isPaid}
      onClose={() =>
        dispatch({
          type: paymentReducerActionTypes.UPDATE_SHOW_PAYMENT_MODAL,
          payload: false,
        })
      }
    >
      {state.booking.date && (
        <Heading color="#fff" mb="1">
          {formatBookingDate({
            date: state.booking.date,
            timezone: shop?.timezoneName,
            language,
          })}
        </Heading>
      )}
      {state.booking.startTime && state.booking.endTime && (
        <Heading highLight mb="2">
          {`${sailoutTimeFormatted} - ${returnTimeFormatted}`}
        </Heading>
      )}

      {state.booking.shopId === shopIds.GOBOAT_CANAL_TOURS ? (
        <PeopleDurationSummary
          boatCount={state.booking.boatCount}
          duration={state.booking.duration}
          price={bookingPrices.rentalPrice}
        />
      ) : (
        <BoatCountDurationSummary
          boatCount={state.booking.boatCount}
          duration={state.booking.duration}
          price={bookingPrices.rentalPrice}
        />
      )}

      {state.booking.pickupLocationId && state.booking.dropoffLocationId && (
        <FlexibleRoutingSummary
          pickupLocation={dockingLocations?.find(
            (dl) => dl.id === state.booking.pickupLocationId
          )}
          dropoffLocation={dockingLocations?.find(
            (dl) => dl.id === state.booking.dropoffLocationId
          )}
          pickupPrice={bookingPrices.pickupPrice}
          dropoffPrice={bookingPrices.dropoffPrice}
        />
      )}
    </Summary>
  );
};

export default SummaryWithData;
